import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';
import { msg, useApptActions } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import type { Answers } from '../lib/anamnese.ts';
import { can, useAppt, useClient, useMe, useRefresh, type ApptDetail } from '../lib/data.ts';
import { addDays, agoLabel, dayLabel, first, isoLocal, money, nowLocal, toMin } from '../lib/format.ts';
import { CareBlock, Chips, Gr } from '../ui/bits.tsx';
import { absolute, confirmationText, message, sendWhatsAppAfter } from '../lib/messages.ts';
import { bookingPath } from '../screens/Engage.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';

const ST_LABEL: Record<string, string> = { pending: 'A confirmar', confirmed: 'Confirmada', arrived: 'Na recepção', in_service: 'Em atendimento', completed: 'Concluído', cancelled: 'Cancelado', no_show: 'Não veio' };
const ST_CLS: Record<string, string> = { completed: 'done' };
const METHOD: Record<string, string> = { pix: 'Pix', cash: 'Dinheiro', debit: 'Débito', credit: 'Crédito', client_credit: 'Crédito da cliente', later: 'Pagar depois' };

export function ApptSheet({ id }: { id: string }) {
  const q = useAppt(id);
  const [panel, setPanel] = useState<'' | 'resched' | 'falta' | 'cancel' | 'excluir'>('');
  if (!q.data) return <Sheet title="Horário">{q.isError ? <Failed compact error={q.error} retry={() => q.refetch()} /> : <Skeleton kind="sheet" rows={4} />}</Sheet>;
  const a = q.data;
  if (panel === 'resched') return <Resched a={a} back={() => setPanel('')} />;
  if (panel === 'falta') return <Falta a={a} back={() => setPanel('')} />;
  if (panel === 'cancel') return <Cancel a={a} back={() => setPanel('')} />;
  if (panel === 'excluir') return <Excluir a={a} back={() => setPanel('')} />;
  return <Main a={a} setPanel={setPanel} />;
}

const asItem = (a: ApptDetail) => ({ id: a.id, client: a.client.name, phone: a.client.phone, status: a.status, time: a.time });

function Main({ a, setPanel }: { a: ApptDetail; setPanel: (p: '' | 'resched' | 'falta' | 'cancel' | 'excluir') => void }) {
  const nav = useNav();
  const me = useMe().data!;
  const act = useApptActions();
  const toast = useToast();
  const cli = useClient(a.client.id).data;
  const now = nowLocal();
  const isToday = a.date === now.date;
  // sem recepção: horário de hoje (ou de dia que passou) vai direto para Receber
  const direct = !me.salon.settings.hasReception && a.date <= now.date;
  const live = a.status !== 'cancelled' && a.status !== 'no_show';
  const it = asItem(a);
  // mensagem pronta com o link "meu horário" (confirmar, remarcar ou cancelar), criado na hora
  const vars = { nome: first(a.client.name), servico: a.serviceLabel, profissional: first(a.professional.name), dia: dayLabel(a.date, now.date).toLowerCase(), hora: a.time };
  const sendLink = () => sendWhatsAppAfter(a.client.phone, () => confirmationText(me.salon.settings, vars, async () => (await api.post<{ path: string }>(`/appointments/${a.id}/link`)).path))
    .catch((e) => toast(msg(e), 'warn'));
  const sendFalta = () => sendWhatsAppAfter(a.client.phone, async () => message(me.salon.settings.messages, 'falta', { ...vars, link: me.salon.settings.onlineBooking === false ? '' : absolute(bookingPath(me.salon.slug)) })).catch((e) => toast(msg(e), 'warn'));
  const sub = `${a.serviceLabel} · ${dayLabel(a.date, now.date)} · ${a.time} às ${a.endTime}`;
  const answers = (cli?.anamnesis?.answers ?? null) as Answers | null;
  const last = cli?.history.find((h) => h.status === 'completed' && h.id !== a.id && h.startsAt < a.startsAt);
  const lastDays = last ? Math.floor((Date.now() - new Date(last.startsAt).getTime()) / 86_400_000) : 0;
  const deposit = a.depositCents;

  const go = async (p: Promise<unknown>, closeAfter = true) => { const ok = await p; if (ok && closeAfter) nav.close(); };

  let primary: React.ReactNode = null;
  if (a.status === 'pending' && a.depositStatus !== 'awaiting') primary = <button className="cta" onClick={() => go(act.confirm(it), false)}>Confirmar horário</button>;
  else if (a.status === 'confirmed' && direct) primary = <button className="cta" onClick={() => nav.open({ kind: 'comanda', id: a.id })}>Receber</button>;
  else if (a.status === 'confirmed' && isToday && toMin(a.time) - now.min <= 30) primary = <button className="cta" onClick={() => go(act.arrived(it))}>Chegou</button>;
  else if (a.status === 'arrived') primary = <button className="cta" onClick={() => go(act.start(it))}>Iniciar atendimento</button>;
  else if (a.status === 'in_service') primary = <button className="cta" onClick={() => nav.open({ kind: 'comanda', id: a.id })}>Receber</button>;
  else if (a.status === 'completed') primary = <button className="cta ghost" onClick={() => nav.open({ kind: 'novo', clientId: a.client.id })}>Agendar a próxima visita</button>;
  const links: React.ReactNode[] = [];
  if (a.status === 'pending' || a.status === 'confirmed') {
    links.push(<button key="r" onClick={() => setPanel('resched')}>Remarcar</button>);
    if (a.status === 'pending' && direct) links.push(<button key="p" onClick={() => nav.open({ kind: 'comanda', id: a.id })}>Receber</button>);
    if (isToday && !(a.status === 'confirmed' && !direct && toMin(a.time) - now.min <= 30)) links.push(<button key="c" onClick={() => go(act.arrived(it))}>Chegou</button>);
    // horário de hoje: Não veio sempre à mão; Cancelar horário continua ao lado
    if (isToday) links.push(<button key="n" className="red" onClick={() => (a.depositStatus === 'received' ? setPanel('falta') : go(act.noShow(it)))}>Não veio</button>);
    if (can(me, 'cancelar')) links.push(<button key="x" className="red" onClick={() => setPanel('cancel')}>Cancelar horário</button>);
  }
  // horário criado por engano (teste, duplicado): some da agenda e do histórico. Só sem dinheiro envolvido
  if (live && a.depositStatus !== 'received' && can(me, 'cancelar')) links.push(<button key="d" className="red" onClick={() => setPanel('excluir')}>Excluir</button>);
  let actions: React.ReactNode;
  if (live) {
    actions = (
      <>
        {primary}
        {(a.status === 'pending' || a.status === 'confirmed') && a.client.whatsapp && <div className="actions-1" style={primary ? { marginTop: 8 } : undefined}><button className="cta ghost" onClick={sendLink}>{a.status === 'pending' ? 'Pedir confirmação no WhatsApp' : 'Mandar o link do horário'}</button></div>}
        {links.length > 0 && <div className={'ap-links' + (primary ? '' : ' alone')}>{links}</div>}
        {a.status === 'arrived' && <button className="link-back" onClick={() => go(api.post(`/appointments/${a.id}/status`, { to: 'confirmed' }).then(() => true))}>Ainda não chegou</button>}
      </>
    );
  } else if (a.status === 'no_show' && isToday) {
    actions = <><button className="cta" onClick={() => go(act.reopen(it))}>Ela chegou · atender agora</button><button className="link-back" onClick={() => nav.open({ kind: 'novo', clientId: a.client.id })}>Agendar outro horário</button>{a.client.whatsapp && <button className="link-back" onClick={sendFalta}>Mandar "Sentimos sua falta"</button>}</>;
  } else {
    actions = <button className="cta ghost" onClick={() => nav.open({ kind: 'novo', clientId: a.client.id })}>Agendar novamente</button>;
  }

  return (
    <Sheet title={a.client.name} sub={sub} chip={<span className={`ap-st st-${ST_CLS[a.status] ?? a.status}`}>{ST_LABEL[a.status]}</span>} actions={actions}>
      <DepositPending a={a} />
      <CareBlock clientId={a.client.id} name={a.client.name} answers={answers} compact />
      <div className="ap-contact">
        <button onClick={() => act.remind(it, `Oi, ${first(a.client.name)}!`)} disabled={!a.client.whatsapp} title={a.client.whatsapp ? undefined : `${first(a.client.name)} não autorizou mensagens`}><Icon name="message" />WhatsApp</button>
        <button disabled={!a.client.phone} onClick={() => { window.location.href = `tel:${a.client.phone.replace(/\D/g, '')}`; }}><Icon name="phone" />Ligar</button>
        <button onClick={() => { nav.go('cliente', { id: a.client.id }); }}><Icon name="user" />Ficha</button>
      </div>
      <p className="gl-title">Atendimento</p>
      <div className="gl">
        {a.extras.length
          ? <>
              <Gr label={a.service.name} value={money(a.mainPriceCents)} cls="ap-svc" />
              {a.extras.map((x) => <Gr key={x.id} label={x.name} value={money(x.priceCents)} cls="ap-svc" />)}
              <Gr label="Total" value={money(a.priceCents)} />
            </>
          : <Gr label="Serviço" value={a.serviceLabel} cls="ap-svc" />}
        {me.professionalId !== a.professional.id && <Gr label="Com" value={a.professional.name} />}
        <Gr label="Origem" value={a.source === 'public' ? 'Agendou online' : a.source === 'import' ? 'Veio do Trinks' : 'Agendado pela equipe'} />
        {deposit > 0 && <Gr label="Sinal" value={`${money(deposit)} · ${{ awaiting: 'aguardando o Pix', received: 'pago', applied: 'já abatido', credited: 'virou crédito', retained: 'retido', refunded: 'devolvido', none: '' }[a.depositStatus]}`} />}
        {a.order && <Gr label="Pagamento" value={`${money(a.order.totalCents)}${a.order.status === 'reversed' ? ' · estornado' : ''}`} />}
        {a.payments.filter((p) => p.kind === 'order').map((p) => <Gr key={p.id} label={METHOD[p.method] ?? p.method} value={money(p.amountCents) + (p.installments > 1 ? ` em ${p.installments}x` : '')} />)}
        {a.arrivedAt && <Gr label="Chegada" value={`${isoLocal(a.arrivedAt).time}${(a.lateMin ?? 0) > 0 ? ` · ${a.lateMin} min de atraso` : ' · no horário'}`} />}
        {a.startedAt && <Gr label="Início" value={isoLocal(a.startedAt).time} />}
        {a.finishedAt && a.startedAt && <Gr label="Duração" value={`${Math.round((new Date(a.finishedAt).getTime() - new Date(a.startedAt).getTime()) / 60_000)} min · previsto ${a.service.durationMin}`} />}
        {a.status === 'cancelled' && a.cancelReason && <Gr label="Cancelado" value={a.cancelReason} />}
      </div>
      <Obs a={a} />
      {a.order && a.order.status === 'paid' && can(me, 'estornar') && <div className="actions-1" style={{ marginTop: 10 }}><button className="cta ghost" onClick={() => nav.open({ kind: 'estorno', orderId: a.order!.id })}>Estornar ou corrigir cobrança</button></div>}
      <p className="gl-title">Sobre a {first(a.client.name)}</p>
      <div className="gl">
        <Gr label="Última visita" value={last ? `${agoLabel(lastDays)} · ${last.service}` : 'Primeira visita'} />
        {a.client.notes && <Gr label="Preferência" value={a.client.notes} />}
        {cli && cli.debt > 0 && <Gr label="Pendência" value={<span className="late">deve {money(cli.debt)}</span>} extra={<button className="gl-act" onClick={() => nav.open({ kind: 'divida', clientId: a.client.id })}>Receber</button>} />}
        {cli && cli.credit > 0 && <Gr label="Crédito" value={money(cli.credit)} />}
        {!a.client.whatsapp && <Gr label="Mensagens" value="não autorizou WhatsApp" />}
        {can(me, 'anamnese') && (
          <Gr label="Anamnese" value={cli?.anamnesis ? `Preenchida em ${new Date(cli.anamnesis.createdAt).toLocaleDateString('pt-BR')}` : 'Ainda não preenchida'}
            extra={<button className="gl-act" onClick={() => nav.open({ kind: 'anamnese', clientId: a.client.id })}>{cli?.anamnesis ? 'Ver' : 'Preencher'}</button>} />
        )}
      </div>
    </Sheet>
  );
}

// anotações do horário: ficam na ficha e aparecem na agenda
function Obs({ a }: { a: ApptDetail }) {
  const toast = useToast();
  const refresh = useRefresh();
  const [text, setText] = useState(a.notes);
  const [busy, setBusy] = useState(false);
  const dirty = text.trim() !== a.notes.trim();
  async function save() {
    setBusy(true);
    try { await api.put(`/appointments/${a.id}/notes`, { notes: text.trim() }); await refresh(); toast(text.trim() ? 'Observação salva.' : 'Observação removida.'); } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }
  return (
    <>
      <p className="gl-title">Observação</p>
      <div className="ap-obs">
        <textarea className="text-in obs-in" rows={3} maxLength={1000} aria-label="Observação do horário" value={text} onChange={(e) => setText(e.target.value)} placeholder="Anotações sobre este horário" />
        {dirty && <button className="cta ghost" disabled={busy} onClick={save}>{busy ? 'Salvando…' : 'Salvar observação'}</button>}
      </div>
    </>
  );
}

function DepositPending({ a }: { a: ApptDetail }) {
  const act = useApptActions();
  const nav = useNav();
  if (a.depositStatus !== 'awaiting') return null;
  const it = asItem(a);
  const due = a.depositDueAt ? isoLocal(a.depositDueAt).time : '';
  const overdue = a.depositDueAt && new Date(a.depositDueAt) < new Date();
  if (overdue) return (
    <div className="sinal-pend venc"><b>O prazo do sinal venceu às {due}</b><small>O Pix de {money(a.depositCents)} ainda não foi conferido. Você decide:</small>
      <div className="sinal-acts"><button className="cta sm" onClick={() => act.depositReceived(it)}>Sinal recebido</button><button className="cta ghost sm" onClick={() => act.depositExtend(it)}>Dar mais 1 hora</button></div>
      <button className="link-back sp-lib" onClick={async () => { if (await act.depositRelease(it)) nav.close(); }}>Liberar o horário</button></div>
  );
  return (
    <div className="sinal-pend"><b>Aguardando o Pix do sinal · {money(a.depositCents)}</b><small>Ela diz que pagou. Confira no app do banco{due ? ` até ${due}` : ''}; se não chegar, o horário é liberado.</small>
      <div className="sinal-acts"><button className="cta sm" onClick={() => act.depositReceived(it)}>Sinal recebido</button><button className="cta ghost sm" onClick={async () => { if (await act.depositRelease(it)) nav.close(); }}>Não recebi</button></div></div>
  );
}

// horários livres perto do original, nos próximos dias
function Resched({ a, back }: { a: ApptDetail; back: () => void }) {
  const nav = useNav();
  const act = useApptActions();
  const now = nowLocal();
  const days = Array.from({ length: 7 }, (_, i) => addDays(now.date >= a.date ? now.date : a.date, i));
  const qs = useQueries({
    queries: days.map((d) => ({
      queryKey: ['availability', d, a.service.id, a.professional.id, a.id],
      queryFn: () => api.get<{ professionals: Array<{ id: string; slots: string[] }> }>(`/availability?date=${d}&serviceId=${a.service.id}&professionalId=${a.professional.id}&ignore=${a.id}`),
    })),
  });
  const want = toMin(a.time);
  const opts: Array<{ date: string; time: string }> = [];
  qs.forEach((q, i) => {
    if (opts.length >= 3) return;
    const slots = (q.data?.professionals[0]?.slots ?? []).filter((t) => !(days[i] === a.date && t === a.time));
    const best = slots.sort((x, y) => Math.abs(toMin(x) - want) - Math.abs(toMin(y) - want))[0];
    if (best) opts.push({ date: days[i]!, time: best });
  });
  const loading = qs.some((q) => q.isLoading);
  return (
    <Sheet title={`Remarcar ${first(a.client.name)}`} sub={`${a.serviceLabel} · ${dayLabel(a.date, now.date)} · ${a.time}`}
      actions={<><button className="cta ghost" onClick={() => nav.open({ kind: 'novo', clientId: a.client.id, rescheduleId: a.id, professionalId: a.professional.id })}>Outro dia ou horário</button><button className="link-back" onClick={back}>Voltar</button></>}>
      <p className="gl-title first">Horários livres com {a.professional.name}, perto das {a.time}</p>
      <div className="rb-slots resched-slots">
        {opts.map((o) => (
          <button key={o.date + o.time} onClick={async () => { if (await act.reschedule(asItem(a), o.date, o.time)) nav.close(); }}><small>{dayLabel(o.date, now.date)}</small><b>{o.time}</b></button>
        ))}
      </div>
      <p className="m-hint">{loading ? 'Procurando horários…' : opts.length ? 'Tocar num horário já remarca.' : 'Nenhum horário livre nos próximos 7 dias.'}</p>
    </Sheet>
  );
}

function Falta({ a, back }: { a: ApptDetail; back: () => void }) {
  const nav = useNav();
  const me = useMe().data!;
  const act = useApptActions();
  const toast = useToast();
  const refresh = useRefresh();
  const [refundOpen, setRefundOpen] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const split = me.salon.settings.deposit.retainedSplit === 'split';
  const why = reason === 'outro' ? other.trim() : reason;
  const refund = async () => {
    try {
      await api.post(`/appointments/${a.id}/status`, { to: 'no_show' });
      await api.post(`/appointments/${a.id}/deposit`, { action: 'refund', reason: why });
      await refresh();
      toast(`Falta de ${first(a.client.name)} registrada. Sinal de ${money(a.depositCents)} devolvido.`, 'warn');
      nav.close();
    } catch (e) { toast(msg(e), 'warn'); }
  };
  return (
    <Sheet title="Não veio" sub={`${a.client.name} · ${a.time}`}
      actions={refundOpen
        ? <><button className="cta" disabled={!why || why.length < 3} onClick={refund}>Devolver {money(a.depositCents)} e registrar a falta</button><button className="link-back" onClick={() => setRefundOpen(false)}>Voltar</button></>
        : <><button className="cta" onClick={async () => { if (await act.noShow(asItem(a))) nav.close(); }}>Registrar falta</button><button className="link-back" onClick={() => setRefundOpen(true)}>Devolver o sinal (caso excepcional)</button><button className="link-back" onClick={back}>Voltar</button></>}>
      <p className="gl-title first">Para onde vai o sinal</p>
      <div className="gl">
        <Gr label="Sinal" value={`${money(a.depositCents)} fica retido: ela perdeu o prazo de cancelamento`} />
        <Gr label="Divisão" value={split ? `${a.professional.name} recebe a parte da cota; o salão fica com o restante` : 'Fica com o salão (Regras do salão)'} />
      </div>
      {refundOpen && (
        <>
          <p className="gl-title">Por que devolver? <span>só em caso excepcional</span></p>
          <Chips value={reason} options={[['Problema de saúde', 'Problema de saúde'], ['Imprevisto da Sublime', 'Imprevisto da Sublime'], ['outro', 'Outro motivo']]} onChange={setReason} />
          {reason === 'outro' && <input className="text-in why-in" autoFocus autoComplete="off" placeholder="Qual foi o motivo?" value={other} onChange={(e) => setOther(e.target.value)} />}
          <p className="m-hint">O valor sai do caixa como devolução e a falta continua registrada.</p>
        </>
      )}
    </Sheet>
  );
}

function Excluir({ a, back }: { a: ApptDetail; back: () => void }) {
  const nav = useNav();
  const toast = useToast();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/appointments/${a.id}/delete`, {}); await refresh(); toast(`Horário de ${first(a.client.name)} excluído.`); nav.close(); } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  };
  return (
    <Sheet title="Excluir horário" sub={`${a.client.name} · ${a.time}`} onBack={back}
      actions={<><button className="cta red" disabled={busy} onClick={go}>{busy ? 'Excluindo…' : `Excluir horário de ${first(a.client.name)}`}</button><button className="link-back" onClick={back}>Voltar</button></>}>
      <p className="m-hint" style={{ marginTop: 0 }}>Use para horário criado por engano ou de teste. Ele some da agenda e do histórico da cliente e o horário fica livre.</p>
      <p className="m-hint">Se a cliente só não vai mais, prefira Cancelar horário: fica registrado. Horário com pagamento não pode ser excluído; estorne o pagamento antes.</p>
    </Sheet>
  );
}

function Cancel({ a, back }: { a: ApptDetail; back: () => void }) {
  const nav = useNav();
  const me = useMe().data!;
  const act = useApptActions();
  const [reason, setReason] = useState<'cliente' | 'salao' | 'outro' | null>(null);
  const [note, setNote] = useState('');
  const [alsoNext, setAlsoNext] = useState(false);
  const rules = me.salon.settings;
  const hours = (new Date(a.startsAt).getTime() - Date.now()) / 3_600_000;
  const hasDeposit = a.depositStatus === 'received';
  const policy = !hasDeposit ? '' : reason === 'salao' ? `O sinal de ${money(a.depositCents)} vira crédito, porque o cancelamento é da Sublime.`
    : hours >= rules.cancelWindowHours ? `Faltam mais de ${rules.cancelWindowHours}h: o sinal de ${money(a.depositCents)} vira crédito para a cliente.`
    : `Faltam menos de ${rules.cancelWindowHours}h: o sinal de ${money(a.depositCents)} fica retido${rules.deposit.retainedSplit === 'split' ? ` e é dividido com ${a.professional.name} pela cota.` : ' pelo salão.'}`;
  const text = reason === 'cliente' ? 'A cliente pediu' : reason === 'salao' ? 'Imprevisto da Sublime' : note.trim();
  const ok = !!reason && (reason !== 'outro' || note.trim().length >= 3);
  return (
    <Sheet title="Cancelar horário" sub={`${a.client.name} · ${a.time} às ${a.endTime}`}
      actions={<><button className="cta danger" disabled={!ok} onClick={async () => { if (await act.cancel(asItem(a), text, reason === 'salao', alsoNext)) nav.close(); }}>{alsoNext ? 'Cancelar este e os próximos' : `Cancelar horário de ${first(a.client.name)}`}</button><button className="link-back" onClick={back}>Voltar</button></>}>
      <p className="gl-title first">Por que cancelar?</p>
      <Chips value={reason} options={[['cliente', 'A cliente pediu'], ['salao', 'Imprevisto da Sublime'], ['outro', 'Outro motivo']]} onChange={setReason} />
      {reason === 'outro' && <input className="text-in why-in" autoFocus autoComplete="off" placeholder="Qual foi o motivo?" value={note} onChange={(e) => setNote(e.target.value)} />}
      {a.seriesId && <div className="toggle-line" style={{ marginTop: 12 }}><span><strong>Cancelar também os próximos</strong><small>Este horário se repete. Os que ainda não aconteceram saem junto.</small></span>
        <button role="switch" aria-checked={alsoNext} aria-label="Cancelar também os próximos" onClick={() => setAlsoNext(!alsoNext)}><Icon name={alsoNext ? 'toggleOn' : 'toggleOff'} /></button></div>}
      {policy && <div className="note warn"><Icon name="info" /><span>{policy}</span></div>}
    </Sheet>
  );
}

